import { useDebouncedValue } from '@mantine/hooks'
import type { SeenMedplumClient } from '@seen/core/client'
import { ICD10_CM_VALUE_SET } from '@seen/core/codeSystems'
import { normalizeConditionTemplateTitle } from '@seen/core/models/organizationConditionsList'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export const ICD10_SEARCH_RESULT_LIMIT = 20

export interface Icd10Result {
  code: string
  name: string
}

interface UseIcd10SearchOptions {
  query: string
  enabled: boolean
  existingParticipantIcd10Codes: Set<string>
  existingOrganizationConditionTitles: Set<string>
}

interface UseIcd10SearchResult {
  results: Icd10Result[]
  isLoading: boolean
}

export async function fetchIcd10Results(
  medplum: SeenMedplumClient,
  input: string,
  signal?: AbortSignal,
  count = 100
): Promise<Icd10Result[]> {
  const valueSet = await medplum.valueSetExpand(
    {
      url: ICD10_CM_VALUE_SET,
      filter: input,
      count,
    },
    { signal }
  )
  return (valueSet.expansion?.contains ?? []).flatMap((entry) =>
    entry.code && entry.display
      ? [{ code: entry.code, name: entry.display }]
      : []
  )
}

export function useIcd10Search({
  query,
  enabled,
  existingParticipantIcd10Codes,
  existingOrganizationConditionTitles,
}: UseIcd10SearchOptions): UseIcd10SearchResult {
  const medplum = useMedplum()
  const [debouncedQuery] = useDebouncedValue(query, 300)
  const trimmed = debouncedQuery.trim()

  const { data, isLoading } = useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps -- medplum is stable
    queryKey: ['icd10-search', trimmed],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchIcd10Results(medplum, trimmed, signal),
    enabled: enabled && trimmed.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const filtered = (data ?? []).filter(
    (result: Icd10Result) =>
      !existingParticipantIcd10Codes.has(result.code) &&
      !existingOrganizationConditionTitles.has(
        normalizeConditionTemplateTitle(result.name)
      )
  )

  return {
    results: filtered,
    isLoading: enabled && trimmed.length > 0 && isLoading,
  }
}
